import { useQuery } from '@tanstack/react-query';
import { MSym } from '@storeo/ui';
import { api } from '../lib/api';
import { DemoBanner, PlaceholderTag } from '../components/lumina/Placeholder';

function money(v: number): string {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function pct(part: number, total: number): string {
  return total ? `${((part / total) * 100).toFixed(1)}%` : '—';
}

/** Admin · Métricas & BI — nova (Fluxo 4 Fase 2). Dado real: o funil de 30 dias (mesmo
 *  `GET /api/admin/overview`, reaproveitado com outra lente: passos + mix de pagamento
 *  PIX×Cartão). O protótipo descreve ML (ARIMA+Transformers), cohort de retenção e
 *  segmentação por nicho — nada disso existe, fica marcado "exemplo". */
export function Metricas() {
  const overview = useQuery({ queryKey: ['admin', 'overview'], queryFn: () => api.overview() });
  const f = overview.data?.funnel30d;
  const paidTotal = (f?.paidPix ?? 0) + (f?.paidCard ?? 0);

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-surface-container-low p-space-xl shadow-xl">
        <div className="relative z-10 flex flex-col gap-space-2xs">
          <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold">Métricas & BI</h1>
          <p className="font-body-md text-body-md text-on-surface-variant">Funil de conversão e mix de pagamento dos últimos 30 dias.</p>
        </div>
      </div>

      {!f ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">Carregando…</p>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-space-lg">
          <section className="xl:col-span-8 flex flex-col p-space-lg rounded-xl bg-surface-container-low shadow-md gap-space-md">
            <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Funil de Conversão (30 dias)</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-space-sm">
              <FunnelStep label="Visualizações" value={f.pageViews} sub="topo do funil" />
              <FunnelStep label="Checkouts iniciados" value={f.checkoutsStarted} sub={pct(f.checkoutsStarted ?? 0, f.pageViews ?? 0)} />
              <FunnelStep label="Pagos via PIX" value={f.paidPix} sub={pct(f.paidPix ?? 0, f.checkoutsStarted ?? 0)} />
              <FunnelStep label="Pagos via cartão" value={f.paidCard} sub={pct(f.paidCard ?? 0, f.checkoutsStarted ?? 0)} tone="secondary" />
            </div>
          </section>

          <section className="xl:col-span-4 flex flex-col p-space-lg rounded-xl bg-surface-container-low shadow-md gap-space-md">
            <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Mix de Meios de Pagamento</h2>
            {paidTotal === 0 ? (
              <p className="font-body-sm text-body-sm text-on-surface-variant">Sem pagamentos aprovados no período.</p>
            ) : (
              <div className="flex flex-col gap-space-sm">
                <MixBar label="PIX Instantâneo" value={f.paidPix ?? 0} total={paidTotal} tone="secondary" />
                <MixBar label="Cartão de Crédito" value={f.paidCard ?? 0} total={paidTotal} tone="primary" />
                <div className="pt-space-xs border-t border-surface-container-highest/40 flex items-center justify-between">
                  <span className="font-label-sm text-label-sm text-outline uppercase">Total pago</span>
                  <span className="font-headline-sm text-headline-sm text-secondary font-bold font-mono">R$ {money((f.paidAmountCents ?? 0) / 100)}</span>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      <DemoBanner>
        O protótipo descreve modelo preditivo ML (ARIMA + Transformers), cohort de retenção de lojistas e segmentação por nicho de mercado — a
        Storeo não tem esses recursos hoje. Seções abaixo são ilustrativas.
      </DemoBanner>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-space-lg">
        <section className="flex flex-col p-space-lg rounded-xl bg-surface-container-low shadow-md gap-space-sm">
          <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold flex items-center gap-1">
            <MSym name="model_training" className="text-[18px] text-primary" /> Projeção Algorítmica (ML) <PlaceholderTag />
          </h2>
          <p className="font-body-sm text-body-sm text-on-surface-variant">Modelo de série temporal calibrado sobre a sazonalidade da rede.</p>
          <div className="h-32 rounded-xl bg-surface-container-lowest flex items-center justify-center text-outline font-label-sm text-label-sm">
            Sem motor de projeção — não existe hoje
          </div>
        </section>
        <section className="flex flex-col p-space-lg rounded-xl bg-surface-container-low shadow-md gap-space-sm">
          <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold flex items-center gap-1">
            <MSym name="grid_view" className="text-[18px] text-tertiary" /> Retenção de Lojistas por Safra (Cohort) <PlaceholderTag />
          </h2>
          <p className="font-body-sm text-body-sm text-on-surface-variant">Percentual de atividade por mês de adesão.</p>
          <div className="h-32 rounded-xl bg-surface-container-lowest flex items-center justify-center text-outline font-label-sm text-label-sm">
            Sem análise de cohort — não existe hoje
          </div>
        </section>
      </div>
    </>
  );
}

function FunnelStep({ label, value, sub, tone = 'primary' }: { label: string; value: number | undefined; sub: string; tone?: string }) {
  return (
    <div className="rounded-xl bg-surface-container-lowest p-space-md flex flex-col gap-space-2xs">
      <span className="font-label-sm text-label-sm uppercase tracking-wider text-outline">{label}</span>
      <span className={`font-headline-sm text-headline-sm font-bold ${tone === 'secondary' ? 'text-secondary' : 'text-on-surface'}`}>{value ?? 0}</span>
      <span className="text-[11px] text-on-surface-variant">{sub}</span>
    </div>
  );
}

function MixBar({ label, value, total, tone }: { label: string; value: number; total: number; tone: 'secondary' | 'primary' }) {
  const p = total ? (value / total) * 100 : 0;
  return (
    <div className="flex flex-col gap-space-3xs">
      <div className="flex justify-between font-label-sm text-label-sm">
        <span className="text-on-surface font-semibold">{label}</span>
        <span className={tone === 'secondary' ? 'text-secondary font-bold' : 'text-primary font-bold'}>{p.toFixed(1)}%</span>
      </div>
      <div className="w-full bg-surface-container-highest rounded-full h-2 overflow-hidden">
        <div className={`h-full rounded-full ${tone === 'secondary' ? 'bg-secondary' : 'bg-primary'}`} style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}
